import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
  DeleteOrderDialogResult,
  OrderService,
  OrderStatus,
  UpdateOrderResult,
  filterServicesByOrderId,
  filterServicesByOrderNumber,
  formatUserMessageText,
  getOrderMutationBlockedMessage,
  getServiceOrderId,
  isOrderStatusDeletable,
  resolveOrderGraphqlUserMessage,
  shouldRefreshAfterOrderError,
} from 'src/app/entities/order';

/**
 * Injected payload for DialogDeleteOSComponent.
 */
export interface DeleteServiceOrderDialogData {
  service: {
    id?: string;
    nombreServicio?: string | null;
    providerSnapshot?: { nombre?: string | null } | null;
    nombreProveedor?: string | null;
    order?: {
      orderId?: string | null;
      orderNumber?: string | number | null;
      orderStatus?: string | null;
      reservationNumber?: string | null;
      reservationStatus?: string | null;
    } | null;
    orderId?: string | null;
    orderNumber?: string | number | null;
    ordenServicioId?: string | null;
    ordenServicioStatus?: string | null;
  };
  dataAll?: any[];
  contextLabel?: string;
}

export type DeleteOrderViewState =
  | 'confirm'
  | 'loading'
  | 'error'
  | 'blocked';

/**
 * Modal dialog for confirming and executing deletion of an Order of Service.
 */
@Component({
  selector: 'app-dialog-delete-os',
  standalone: true,
  templateUrl: './dialog-delete-os.component.html',
  styleUrls: ['./dialog-delete-os.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TablerIconsModule,
  ],
  host: { class: 'delete-order-host' },
})
export class DialogDeleteOSComponent implements OnInit {
  public viewState: DeleteOrderViewState = 'confirm';
  public displayedErrorTitleText: string | null = null;
  public displayedErrorMessageText: string | null = null;
  public doesErrorRequireTableRefresh: boolean = false;

  public readonly targetOrderId: string | null;
  public readonly targetOrderNumber: string | number | null | undefined;
  public readonly displayedServiceName: string;
  public readonly displayedProviderName: string;
  public readonly currentOrderStatus: string | null;
  public readonly currentReservationNumber: string | null;
  public readonly currentReservationStatus: string | null;
  public readonly contextLabelString: string | null;
  public readonly affectedServicesCount: number;

  private deletedOrderResultPayload: UpdateOrderResult | null = null;

  /**
   * Initializes the DialogDeleteOSComponent with injected dialog reference, data, and OrderService.
   *
   * Description:
   *   Extracts target orderId, orderNumber, service and provider details.
   *
   * Parameters:
   *   dialogReference: MatDialogRef<DialogDeleteOSComponent, DeleteOrderDialogResult> - Modal controller.
   *   injectedDialogData: DeleteServiceOrderDialogData - Service and order payload.
   *   orderServiceInstance: OrderService - Order domain API service.
   *
   * Return Value:
   *   DialogDeleteOSComponent - Initialized component instance.
   *
   * Exceptions & Errors:
   *   None.
   */
  constructor(
    private readonly dialogReference: MatDialogRef<
      DialogDeleteOSComponent,
      DeleteOrderDialogResult
    >,
    @Inject(MAT_DIALOG_DATA)
    public readonly injectedDialogData: DeleteServiceOrderDialogData,
    private readonly orderServiceInstance: OrderService,
  ) {
    const targetServiceRecord = injectedDialogData.service;
    this.targetOrderId = getServiceOrderId(targetServiceRecord);
    this.targetOrderNumber =
      targetServiceRecord?.order?.orderNumber ??
      targetServiceRecord?.orderNumber;
    this.displayedServiceName =
      targetServiceRecord?.nombreServicio?.trim() || 'Servicio';
    this.displayedProviderName =
      targetServiceRecord?.providerSnapshot?.nombre?.trim() ||
      targetServiceRecord?.nombreProveedor?.trim() ||
      '-';
    this.currentOrderStatus =
      targetServiceRecord?.order?.orderStatus ??
      targetServiceRecord?.ordenServicioStatus ??
      null;
    this.currentReservationNumber =
      targetServiceRecord?.order?.reservationNumber ?? null;
    this.currentReservationStatus =
      targetServiceRecord?.order?.reservationStatus ?? null;
    this.contextLabelString =
      injectedDialogData.contextLabel ?? null;

    if (this.targetOrderNumber && injectedDialogData.dataAll?.length) {
      const matchingServicesList = filterServicesByOrderNumber(
        injectedDialogData.dataAll,
        this.targetOrderNumber,
      );
      this.affectedServicesCount = matchingServicesList.length || 1;
    } else if (this.targetOrderId && injectedDialogData.dataAll?.length) {
      const matchingServicesList = filterServicesByOrderId(
        injectedDialogData.dataAll,
        this.targetOrderId,
      );
      this.affectedServicesCount = matchingServicesList.length || 1;
    } else {
      this.affectedServicesCount = 1;
    }
  }

  /**
   * Angular Lifecycle hook executed on component initialization.
   *
   * Description:
   *   Verifies whether the order is eligible for deletion, transitioning to blocked state if invalid.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public ngOnInit(): void {
    if (!this.canDelete) {
      this.viewState = 'blocked';
    }
  }

  public get canDelete(): boolean {
    return (
      Boolean(this.targetOrderId) &&
      isOrderStatusDeletable(this.currentOrderStatus)
    );
  }

  public get blockedMessage(): string {
    if (!this.targetOrderId) {
      return 'Este servicio no tiene una Orden de Servicio asociada.';
    }

    return getOrderMutationBlockedMessage(
      'eliminar',
      this.currentOrderStatus,
    );
  }

  public get statusBadgeClass(): string {
    const normalizedStatus =
      this.currentOrderStatus?.toUpperCase();
    if (normalizedStatus === OrderStatus.CONFIRMADO) {
      return 'os-badge os-badge--confirmado';
    }
    if (normalizedStatus === OrderStatus.EN_PROCESO) {
      return 'os-badge os-badge--proceso';
    }
    if (normalizedStatus === OrderStatus.PROCESADO) {
      return 'os-badge os-badge--procesado';
    }
    return 'os-badge';
  }

  /**
   * Dispatches deleteOrder mutation upon user confirmation.
   *
   * Description:
   *   Sets viewState to loading, executes mutation, and closes dialog on success.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Maps GraphQL errors via resolveOrderGraphqlUserMessage.
   */
  public confirmDelete(): void {
    if (!this.canDelete || this.viewState === 'loading') {
      return;
    }

    this.viewState = 'loading';
    this.displayedErrorTitleText = null;
    this.displayedErrorMessageText = null;
    this.doesErrorRequireTableRefresh = false;

    this.orderServiceInstance
      .deleteOrder({ orderId: this.targetOrderId! })
      .subscribe({
        next: (updatedOrderPayload: UpdateOrderResult) => {
          this.deletedOrderResultPayload = updatedOrderPayload;
          this.close(true, false);
        },
        error: (deletionErrorPayload: unknown) => {
          const userErrorMessage = resolveOrderGraphqlUserMessage(
            deletionErrorPayload,
            'No se pudo eliminar la Orden de Servicio.',
          );
          this.displayedErrorTitleText = userErrorMessage.title;
          this.displayedErrorMessageText = formatUserMessageText(
            userErrorMessage,
          );
          this.doesErrorRequireTableRefresh =
            shouldRefreshAfterOrderError(deletionErrorPayload);
          this.viewState = 'error';
          if (this.doesErrorRequireTableRefresh) {
            this.dialogReference.disableClose = true;
          }
        },
      });
  }

  public retry(): void {
    this.viewState = 'confirm';
    this.displayedErrorTitleText = null;
    this.displayedErrorMessageText = null;
    this.doesErrorRequireTableRefresh = false;
  }

  public cancel(): void {
    this.close(false, false);
  }

  public dismissBlocked(): void {
    this.close(false, false);
  }

  public cerrarYActualizar(): void {
    this.dialogReference.disableClose = false;
    this.close(false, true);
  }

  /**
   * Closes dialog emitting DeleteOrderDialogResult.
   *
   * Description:
   *   Invokes dialogReference.close with outcome metadata.
   *
   * Parameters:
   *   wasSuccessful: boolean - Whether deletion succeeded.
   *   shouldTriggerRefresh: boolean - Whether table refresh is required.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  private close(
    wasSuccessful: boolean,
    shouldTriggerRefresh: boolean = false,
  ): void {
    this.dialogReference.close({
      success: wasSuccessful,
      shouldRefresh: shouldTriggerRefresh,
      orderNumber: this.targetOrderNumber,
      orderId: this.targetOrderId,
      order: this.deletedOrderResultPayload,
    });
  }
}
